import type { MouseEvent, ReactNode } from 'react';

import type { ToggleButtonGroupProps } from '@mui/material';
import { ToggleButton } from '@mui/material';

import { SegmentedControlGroupStyled } from './segmented-control.styled';

export type SegmentedControlOption<Value extends string> = {
  value: Value;
  label: ReactNode;
};

export type SegmentedControlProps<Value extends string> = Omit<
  ToggleButtonGroupProps,
  'value' | 'onChange' | 'exclusive' | 'children'
> & {
  value: Value;
  options: SegmentedControlOption<Value>[];
  onChange: (value: Value) => void;
};

export function SegmentedControl<Value extends string>(
  props: SegmentedControlProps<Value>,
) {
  const { value, options, onChange, ...rest } = props;

  const handleChange = (_event: MouseEvent<HTMLElement>, newValue: Value | null) => {
    // null means the active segment was clicked again — keep the current selection
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <SegmentedControlGroupStyled
      {...rest}
      exclusive
      value={value}
      onChange={handleChange}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </SegmentedControlGroupStyled>
  );
}
